import { FC, useEffect, useRef, useState } from 'react';
import { mainButton } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { BioTextarea } from '@/components/ProfileData/Inputs/BioTextarea.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';

interface EditBioPopupProps {
  handleClose: () => void;
}

const EditBioPopup: FC<EditBioPopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);

  const { aboutMe, updateProfile } = useProfileStore();
  const { t } = useTranslation();

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [bio, setBio] = useState<string>(aboutMe?.profile.about_me ?? '');
  const sendBioRef = useRef(() => {});
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    sendBioRef.current = async () => {
      mainButton.setParams({
        isLoaderVisible: true,
        isEnabled: false
      });

      try {
        await updateProfile({ about_me: bio });
        helpers.toast({
          id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
          content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
        });
        handleClose();
      } catch {
        helpers.toast({
          id: MessageTypesUI.TYPE_SYSTEM_ERROR,
          content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
        });
      } finally {
        mainButton.setParams({
          isLoaderVisible: false,
          isVisible: false,
          isEnabled: true
        });
        textareaRef.current?.blur();
      }
    };
  }, [bio]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    const handleClick = () => sendBioRef.current();
    mainButton.onClick(handleClick);

    return () => {
      mainButton.offClick(handleClick);
    };
  }, []);

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    if (bio !== aboutMe?.profile.about_me) {
      setIsEdited(true);
    }
  }, [bio, aboutMe?.profile.about_me]);

  useEffect(() => {
    const focusTextarea = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const length = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(length, length);
        textareaRef.current.scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      }
    };

    const timeout = setTimeout(focusTextarea, 100);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        overscrollBehavior: 'none',
        touchAction: 'none',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <BioTextarea
        about_me={bio}
        updateFields={(fields) => setBio(fields.about_me ?? '')}
        ref={textareaRef}
      />
    </div>
  );
};

export default EditBioPopup;
